/**
 * The Linkup — Design System
 * ──────────────────────────────────────────────
 * Adapted from Apple's typography & layout principles.
 *
 * Guiding rules:
 *  1. Every size, space, and color lives HERE — never ad-hoc.
 *  2. Type scale uses a Major Third ratio (1.25) for natural rhythm.
 *  3. Spacing is an 8-point grid. Every margin / padding is a multiple of 8.
 *  4. Motion is fast, purposeful, and physics-based (no linear easing).
 *  5. Surfaces use *subtle* depth, never "card + shadow" for every section.
 */

// ─── Fonts ───────────────────────────────────────────────────────────
// Apple's actual system font stack — native SF Pro on macOS/iOS
export const FONT_DISPLAY = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"SF Pro Display"',
  '"SF Pro Text"',
  '"Helvetica Neue"',
  "sans-serif",
].join(", ");

export const FONT_TEXT = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"SF Pro Display"',
  '"SF Pro Text"',
  '"Helvetica Neue"',
  "sans-serif",
].join(", ");

export const FONT_MONO = ['"SF Mono"', "Menlo", "Consolas", "monospace"].join(
  ", ",
);

// ─── Type Scale ──────────────────────────────────────────────────────
// Named semantically, not by pixel size.
// Each entry: [fontSize, lineHeight, letterSpacing, fontWeight]
export const TYPE = {
  // ── Display — used ONCE per page (hero headline) ──
  displayLarge: {
    fontFamily: FONT_DISPLAY,
    fontSize: "clamp(2.75rem, 6vw, 5rem)", // 44–88 px
    lineHeight: 1.04,
    letterSpacing: "-0.045em",
    fontWeight: 700,
  },
  displayMedium: {
    fontFamily: FONT_DISPLAY,
    fontSize: "clamp(2.25rem, 4.5vw, 4rem)", // 36–64 px
    lineHeight: 1.06,
    letterSpacing: "-0.04em",
    fontWeight: 700,
  },

  // ── Headline — section openers ──
  headline: {
    fontFamily: FONT_DISPLAY,
    fontSize: "clamp(1.75rem, 3.2vw, 3rem)", // 28–48 px
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    fontWeight: 600,
  },
  headlineSmall: {
    fontFamily: FONT_DISPLAY,
    fontSize: "clamp(1.375rem, 2.4vw, 2rem)", // 22–32 px
    lineHeight: 1.14,
    letterSpacing: "-0.022em",
    fontWeight: 600,
  },

  // ── Title — cards, modals ──
  title: {
    fontFamily: FONT_DISPLAY,
    fontSize: "clamp(1.125rem, 1.6vw, 1.5rem)", // 18–24 px
    lineHeight: 1.2,
    letterSpacing: "-0.016em",
    fontWeight: 600,
  },
  titleSmall: {
    fontFamily: FONT_TEXT,
    fontSize: "1rem", // 16 px — fixed
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
    fontWeight: 600,
  },

  // ── Body — readable paragraphs ──
  bodyLarge: {
    fontFamily: FONT_TEXT,
    fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)", // 17–20 px
    lineHeight: 1.52,
    letterSpacing: "-0.005em",
    fontWeight: 400,
  },
  body: {
    fontFamily: FONT_TEXT,
    fontSize: "1rem", // 16 px
    lineHeight: 1.5,
    letterSpacing: "0",
    fontWeight: 400,
  },
  bodySmall: {
    fontFamily: FONT_TEXT,
    fontSize: "0.875rem", // 14 px
    lineHeight: 1.45,
    letterSpacing: "0.005em",
    fontWeight: 400,
  },

  // ── Caption / Legal ──
  caption: {
    fontFamily: FONT_TEXT,
    fontSize: "0.75rem", // 12 px
    lineHeight: 1.4,
    letterSpacing: "0.01em",
    fontWeight: 400,
  },
  overline: {
    fontFamily: FONT_TEXT,
    fontSize: "0.6875rem", // 11 px
    lineHeight: 1.45,
    letterSpacing: "0.06em",
    fontWeight: 600,
    textTransform: "uppercase",
  },
};

// ─── 8-Point Spacing Grid ────────────────────────────────────────────
// Usage: SPACE[2] = 16px. Always multiply by the token, never raw px.
export const SPACE = {
  0: "0px",
  0.5: "4px",
  1: "8px",
  1.5: "12px",
  2: "16px",
  3: "24px",
  4: "32px",
  5: "40px",
  6: "48px",
  8: "64px",
  10: "80px",
  12: "96px",
  16: "128px",
  20: "160px",
  24: "192px",
};

// ─── Layout ──────────────────────────────────────────────────────────
export const LAYOUT = {
  maxWidth: "1120px", // content never wider than this
  maxWidthNarrow: "720px", // for text-heavy sections (legal, about copy)
  maxWidthWide: "1440px", // for full-bleed hero backgrounds
  gutter: { xs: "20px", md: "40px", lg: "80px" },
  borderRadius: {
    xs: "8px", // tiny chips, tags
    sm: "12px", // inputs, small cards
    md: "16px", // cards
    lg: "24px", // feature cards, modals
    xl: "32px", // hero sections, big cards
    full: "9999px", // pills, buttons
  },
  // Section vertical rhythm — the "breathing" between blocks
  sectionGap: {
    xs: SPACE[8], // 64 px on mobile
    md: SPACE[12], // 96 px on tablet
    lg: SPACE[16], // 128 px on desktop
  },
};

// ─── Color Palette ───────────────────────────────────────────────────
// Apple-inspired light mode: clean whites with subtle grays.
export const COLOR = {
  // Backgrounds (layered — never more than 3 levels deep)
  bg0: "#ffffff", // page canvas
  bg1: "#f5f5f7", // primary surface (cards)
  bg2: "#e8e8ed", // elevated surface (modals, dropdowns)
  bgSubtle: "#fafafa", // barely-there tint for alternating rows

  // Text
  textPrimary: "#1d1d1f", // Apple's signature dark text
  textSecondary: "#6e6e73", // subdued but legible
  textTertiary: "#86868b", // captions, timestamps
  textInverse: "#f5f5f7", // text on dark surfaces

  // Brand — Apple uses blue (#0071e3) for links
  brand: "#0071e3", // Apple link blue
  brandHover: "#0077ed",
  brandSubtle: "rgba(0, 113, 227, 0.08)",
  brandGlow: "rgba(0, 113, 227, 0.15)",

  // Borders — subtle, Apple-style
  border: "rgba(0, 0, 0, 0.08)",
  borderHover: "rgba(0, 0, 0, 0.14)",
  borderStrong: "rgba(0, 0, 0, 0.2)",

  // Semantic
  success: "#34c759",
  error: "#ff3b30",
  warning: "#ff9500",
  info: "#007aff",

  // Overlays & Glass
  overlay: "rgba(0, 0, 0, 0.3)",
  glass: "rgba(255, 255, 255, 0.72)",
  glassBorder: "rgba(0, 0, 0, 0.06)",
};

// ─── Motion ──────────────────────────────────────────────────────────
// Apple uses fast, slightly damped curves. No `linear`, no `ease`.
export const MOTION = {
  // Micro-interactions (hover, focus, toggle)
  micro: {
    duration: "0.18s",
    easing: "cubic-bezier(0.25, 0.1, 0.25, 1)",
  },
  // Standard (card entrance, page transitions, modals)
  standard: {
    duration: "0.36s",
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
  // Expressive (hero entrances, large reveals)
  expressive: {
    duration: "0.64s",
    easing: "cubic-bezier(0.16, 1, 0.3, 1)",
  },
  // Spring-like bounce for delight (button press, notification pop)
  spring: {
    duration: "0.5s",
    easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
};

// ─── Elevation (box-shadow) ──────────────────────────────────────────
// Apple uses subtle shadows to make products "sit" above backgrounds
export const ELEVATION = {
  none: "none",
  low: "0 2px 4px rgba(0, 0, 0, 0.08)", // subtle product lift
  medium: "0 4px 12px rgba(0, 0, 0, 0.12)", // cards, modals
  high: "0 20px 40px rgba(0, 0, 0, 0.16)", // Apple product "hover" effect
  glow: "0 0 24px rgba(0, 113, 227, 0.25)", // brand highlight
};

// ─── Reusable SX Mixins ─────────────────────────────────────────────
// Drop these into `sx={{}}` for instant consistency.

/** Full-bleed section wrapper */
export const sxSection = {
  width: "100%",
  maxWidth: LAYOUT.maxWidth,
  mx: "auto",
  px: { xs: "20px", md: "40px", lg: "80px" },
};

/** Narrow column for text-heavy content */
export const sxNarrow = {
  ...sxSection,
  maxWidth: LAYOUT.maxWidthNarrow,
};

/** Standard card surface */
export const sxCard = {
  bgcolor: COLOR.bg1,
  borderRadius: LAYOUT.borderRadius.md,
  border: `1px solid ${COLOR.border}`,
  transition: `border-color ${MOTION.micro.duration} ${MOTION.micro.easing}`,
  "&:hover": {
    borderColor: COLOR.borderHover,
  },
};

/** Glassmorphism surface */
export const sxGlass = {
  bgcolor: COLOR.glass,
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  borderRadius: LAYOUT.borderRadius.lg,
  border: `1px solid ${COLOR.glassBorder}`,
};

/** Page canvas — apply to root Box of every page */
export const sxPageCanvas = {
  bgcolor: COLOR.bg0,
  color: COLOR.textPrimary,
  width: "100%",
  minHeight: "100%",
  fontFamily: FONT_TEXT,
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  overflowY: "auto",
  overflowX: "hidden",
  paddingTop: "env(safe-area-inset-top, 0px)",
};

/** Pill-shaped button base */
export const sxButtonPill = {
  borderRadius: LAYOUT.borderRadius.full,
  textTransform: "none",
  fontFamily: FONT_TEXT,
  fontWeight: 600,
  px: SPACE[3],
  py: SPACE[1.5],
  transition: `all ${MOTION.micro.duration} ${MOTION.micro.easing}`,
};

/** Section vertical spacing */
export const sxSectionSpacing = {
  py: { xs: SPACE[8], md: SPACE[12], lg: SPACE[16] },
};

/** Footer */
export const sxFooter = {
  mt: { xs: SPACE[10], md: SPACE[16] },
  pt: SPACE[4],
  pb: SPACE[4],
  borderTop: `1px solid ${COLOR.border}`,
  color: COLOR.textTertiary,
  ...TYPE.bodySmall,
};

// ─── Legal / Policy Page Styles ──────────────────────────────────────
// Consistent styling for Terms, Privacy, Community Guidelines, etc.

/** Legal page wrapper - full page canvas with proper scrolling */
export const sxLegalPageWrapper = {
  bgcolor: "#ffffff",
  color: "#1d1d1f",
  width: "100%",
  minHeight: "100%",
  fontFamily: "Inter, sans-serif",
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
};

/** Legal page logo container */
export const sxLegalLogoContainer = {
  display: "flex",
  justifyContent: "center",
  pt: "2rem",
  pb: "1rem",
};

/** Legal page logo */
export const sxLegalLogo = {
  width: { xs: "60px", md: "80px" },
  pt: { xs: "2rem", md: "2rem" },
  height: "auto",
};

/** Legal page content container */
export const sxLegalContentContainer = {
  pb: "5rem",
  px: "1.5rem",
  maxWidth: "50rem",
  mx: "auto",
  textAlign: "center",
};

/** Legal page header section */
export const sxLegalHeader = {
  mb: "5rem",
};

/** Legal page title */
export const sxLegalTitle = {
  fontSize: { xs: "2.75rem", md: "4.5rem" },
  fontWeight: 800,
  mb: "1.5rem",
  letterSpacing: "-0.04em",
  lineHeight: 1.1,
  textShadow: "none",
};

/** Legal page subtitle/last updated */
export const sxLegalSubtitle = {
  fontSize: { xs: "1.1rem", md: "1.25rem" },
  color: "rgba(0, 0, 0, 0.6)",
  fontWeight: 400,
  maxWidth: "35rem",
  mx: "auto",
  textShadow: "none",
};

/** Legal page last updated date */
export const sxLegalLastUpdated = {
  display: "block",
  mt: 1,
  fontSize: "0.95rem",
  opacity: 0.7,
};

/** Legal page sections container */
export const sxLegalSections = {
  textAlign: "left",
  "& > section": { mb: "3.5rem" },
};

/** Legal page section heading */
export const sxLegalSectionHeading = {
  fontSize: "1.25rem",
  fontWeight: 700,
  mb: "1.25rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

/** Legal page body text */
export const sxLegalBodyText = {
  color: "rgba(0,0,0,0.7)",
  lineHeight: 1.7,
  fontSize: "1.1rem",
};

/** Legal page intro text (slightly bolder) */
export const sxLegalIntroText = {
  color: "rgba(0,0,0,0.85)",
  fontSize: "1.13rem",
  mb: 1.5,
  fontWeight: 500,
};

/** Legal page list */
export const sxLegalList = {
  pl: "1.25rem",
  color: "rgba(0,0,0,0.7)",
  "& li": { mb: "0.75rem", fontSize: "1.1rem" },
};

/** Legal page link styling */
export const sxLegalLink = {
  color: "#0071e3",
  textDecoration: "underline",
};

/** Legal page email link (prominent) */
export const sxLegalEmailLink = {
  fontSize: "1.25rem",
  color: "#0071e3",
  textDecoration: "none",
  fontWeight: 600,
  borderBottom: "2px solid #0071e3",
  pb: "4px",
  "&:hover": { opacity: 0.7 },
};

/** Legal page footer */
export const sxLegalFooter = {
  borderTop: "1px solid rgba(0,0,0,0.1)",
  mt: 10,
  pt: 6,
};

/** Legal page footer container */
export const sxLegalFooterContainer = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  justifyContent: "space-between",
  alignItems: { xs: "center", md: "center" },
  gap: 4,
};

/** Legal page footer copyright */
export const sxLegalFooterCopyright = {
  fontSize: "0.875rem",
  fontWeight: 600,
  mb: 0.5,
};

/** Legal page footer ownership text */
export const sxLegalFooterOwnership = {
  fontSize: "0.68rem",
  color: "rgba(0,0,0,0.45)",
  mt: 0.2,
};

/** Legal page footer links container */
export const sxLegalFooterLinks = {
  display: "flex",
  gap: 3,
};

/** Legal page footer link */
export const sxLegalFooterLink = {
  fontSize: "0.75rem",
  color: "rgba(0,0,0,0.4)",
  textDecoration: "none",
  transition: "color 0.2s ease",
  "&:hover": { color: "#1d1d1f" },
};
